import { get } from "../../helpers/utils/http-client";
import { APPS_ENDPOINT } from "../../consts/api-endpoints";
import { redactPiiTokens } from "../../helpers/utils/text-util";

const EMPTY_PAGE = { data: [], pagination: {} };

export const SESSION_PAGE_SIZE = 25;
export const SPAN_PAGE_SIZE = 200;

// Page sizes offered in the session list pager.
export const SESSION_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * The backend only honours `requestPage` when `pageSize` is sent alongside it,
 * so both are always supplied together.
 */
const pageParams = (pageSize, requestPage) => {
    const params = { pageSize };
    if (requestPage) {
        params.requestPage = requestPage;
    }
    return params;
};

/**
 * Personal data comes back masked. `reveal` asks for the stored values instead, which
 * the API only grants to a security admin (anyone else gets a 403, shown as an empty page).
 */
const withReveal = (params, reveal) => (reveal ? { ...params, reveal: 'true' } : params);

/**
 * List traced sessions of an app.
 * Backed by the conversation-session store, so sessions carry their human
 * readable labels (refId, displayMessage, handleBy).
 */
export async function getTraceSessions(appId, requestPage, pageSize = SESSION_PAGE_SIZE, reveal = false) {
    try {
        const { response, status } = await get(
            `${APPS_ENDPOINT}/${appId}/traces/sessions`,
            withReveal(pageParams(pageSize, requestPage), reveal));
        if (status !== 200) {
            return EMPTY_PAGE;
        }
        return redactPiiTokens(response);
    } catch (err) {
        console.debug('getTraceSessions cause', err.message, err.code);
        return EMPTY_PAGE;
    }
}

/**
 * The readable timeline of a session: one entry per turn (the triggering
 * message), each an ordered list of events - input, model calls, tool calls and
 * results, workflow steps, errors, reply, end.
 *
 * Returned whole (the backend sends no cursor), so the live-chat transcript can
 * index straight into it by messageId.
 */
export async function getSessionTimeline(appId, sessionId, reveal = false) {
    try {
        const { response, status } = await get(
            `${APPS_ENDPOINT}/${appId}/traces/sessions/${sessionId}`,
            withReveal({ groupBy: 'message', view: 'timeline' }, reveal));
        if (status !== 200) {
            return EMPTY_PAGE;
        }

        // A backend predating the timeline view ignores it and answers with the
        // grouped spans instead. That would render as "no events", so it is
        // called out - the usual cause is an API that has not been redeployed.
        if (response && response.view !== 'timeline') {
            console.warn('tracing API does not support view=timeline yet; deploy the tracing backend');
            return { ...EMPTY_PAGE, unsupported: true };
        }

        return redactPiiTokens(response);
    } catch (err) {
        console.debug('getSessionTimeline cause', err.message, err.code);
        return EMPTY_PAGE;
    }
}

/**
 * List trace events (spans) of a session, oldest first.
 */
export async function getSessionTraces(appId, sessionId, requestPage, view, reveal = false) {
    try {
        const params = withReveal(pageParams(SPAN_PAGE_SIZE, requestPage), reveal);
        // 'user' asks for the curated view: only user-facing steps, with the
        // labelled fields the runtime declared. Omitted means the raw trace.
        if (view) {
            params.view = view;
        }

        const { response, status } = await get(
            `${APPS_ENDPOINT}/${appId}/traces/sessions/${sessionId}`, params);
        if (status !== 200) {
            return EMPTY_PAGE;
        }
        return redactPiiTokens(response);
    } catch (err) {
        console.debug('getSessionTraces cause', err.message, err.code);
        return EMPTY_PAGE;
    }
}
